/**
 * Interceptor registry — axios-style hooks without taking on axios.
 *
 * Two arrays, simple semantics:
 *
 *   request interceptors:  (config) ⇒ config | Promise<config>
 *   response interceptors: (response, requestConfig) ⇒ response
 *   error interceptors:    (error, requestConfig) ⇒ throw | recover
 *
 * The HTTP client iterates over them in registration order. Each
 * interceptor returns a new config / response, or throws to abort.
 *
 *   interceptors.request.use((cfg) => {
 *     const token = getAccessToken()
 *     if (token) cfg.headers.Authorization = `Bearer ${token}`
 *     return cfg
 *   })
 *
 * Use cases this is built for:
 *   - Inject Authorization header (auth interceptor)
 *   - Refresh-on-401 retry loop (refresh interceptor)
 *   - Tenant/workspace header injection
 *   - Trace IDs for observability
 *   - Show a global "saving…" indicator
 *
 * Replace this registry with axios / ky / ofetch later without
 * touching service code; this module is the seam.
 */

import type {
  ErrorInterceptor,
  RequestConfig,
  RequestInterceptor,
  ResponseInterceptor,
} from './types'

export interface Channel<Fn> {
  /** Register an interceptor; the returned function removes it again. */
  use(fn: Fn): () => void
  list(): Fn[]
  clear(): void
}

function makeChannel<Fn>(): Channel<Fn> {
  const fns: Fn[] = []
  return {
    use(fn) {
      fns.push(fn)
      return () => {
        const i = fns.indexOf(fn)
        if (i >= 0) fns.splice(i, 1)
      }
    },
    list() {
      return fns.slice()
    },
    clear() {
      fns.length = 0
    },
  }
}

export const interceptors = {
  request: makeChannel<RequestInterceptor>(),
  response: makeChannel<ResponseInterceptor>(),
  error: makeChannel<ErrorInterceptor>(),
}

/**
 * Run all registered request interceptors over the config in order.
 * Each interceptor can mutate or return a new config.
 */
export async function runRequest(config: RequestConfig): Promise<RequestConfig> {
  let next = config
  for (const fn of interceptors.request.list()) {
    next = (await fn(next)) ?? next
  }
  return next
}

/** Run response interceptors. */
export async function runResponse(response: any, reqConfig: RequestConfig): Promise<any> {
  let next = response
  for (const fn of interceptors.response.list()) {
    next = (await fn(next, reqConfig)) ?? next
  }
  return next
}

/**
 * Run error interceptors. Any may return a Promise that resolves
 * to a recovered response, in which case the chain continues
 * normally. If all rethrow, the original error propagates.
 */
export async function runError(error: unknown, reqConfig: RequestConfig): Promise<any> {
  let last = error
  for (const fn of interceptors.error.list()) {
    try {
      const recovered = await fn(last, reqConfig)
      if (recovered !== undefined) return recovered
    } catch (err) {
      last = err
    }
  }
  throw last
}
